// Opt-in envelopes preserve every existing operation's final response shape.
import axios from "axios";

const paths = [
  /^\/inventory\/(?:import|import\/[^/]+\/(?:commit|refresh)|bulk-edit|bulk-status|bulk-assign-dealer|bulk-publish|deletion\/(?:preview|apply)|block-assign-dealers\/(?:preview|apply))$/,
  /^\/bookings\/legacy\/import\/(?:preview|upload-preview|[^/]+\/(?:commit|refresh))$/,
  /^\/bookings\/legacy\/installments\/(?:upload-preview|[^/]+\/(?:commit|refresh))$/,
  /^\/finance\/payments\/import\/(?:preview|confirm|[^/]+\/refresh)$/,
  /^\/finance\/opening-reconciliations\/evidence-upload\/(?:preview|confirm)$/,
  /^\/accounting\/accounts\/bulk\/(?:parse|preview|confirm)$/,
];
const booking = /^\/(?:bookings|dealer\/booking-creation\/bookings)$/;
const finished = new Set(["COMPLETED", "PARTIAL", "FAILED", "NEEDS_REVIEW", "CANCELLED"]);
const installed = new WeakSet();
export const jobEvent = () => window.dispatchEvent(new Event("background-jobs-change"));
export const backgroundPath = value => paths.some(pattern => pattern.test(value));

function scope() {
  try {
    const part = (localStorage.getItem("bd_token") || "").split(".")[1];
    const data = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return `${data.mid}:${data.sub}`;
  } catch { return "unknown"; }
}
async function hash(value) {
  const bytes = await crypto.subtle.digest("SHA-256", value);
  return [...new Uint8Array(bytes)].map(number => number.toString(16).padStart(2, "0")).join("");
}
async function identity(data) {
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const fields = [];
    for (const [key, value] of data.entries()) fields.push([key, typeof value === "string" ? value : [value.name, value.type, await hash(await value.arrayBuffer())]]);
    return hash(new TextEncoder().encode(JSON.stringify(fields)));
  }
  return hash(new TextEncoder().encode(typeof data === "string" ? data : JSON.stringify(data ?? {})));
}
function savedKey(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function remember(key, value) {
  try { if (value) localStorage.setItem(key, value); else localStorage.removeItem(key); } catch { /* The server still deduplicates captured request keys. */ }
}
function delay(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new axios.CanceledError()); return; }
    const done = () => { signal?.removeEventListener("abort", cancel); resolve(); };
    const timer = setTimeout(done, milliseconds);
    const cancel = () => { clearTimeout(timer); reject(new axios.CanceledError()); };
    signal?.addEventListener("abort", cancel, { once: true });
  });
}

export function installBackgroundJobs(client) {
  if (installed.has(client)) return;
  installed.add(client);
  client.interceptors.request.use(async config => {
    const path = String(config.url || "");
    if (config.method?.toLowerCase() !== "post" || config.backgroundJobs === false) return config;
    const isBooking = booking.test(path);
    if (isBooking && !config._jobRecovery) {
      config._bookingScope = scope();
      config._jobOrigin = window.location.pathname;
      config.timeout = config.timeout || 15000;
      return config;
    }
    if (!backgroundPath(path) && !(isBooking && config._jobRecovery)) return config;
    config.headers = config.headers || {};
    config._jobScope = scope();
    config._jobOrigin = config._jobOrigin || window.location.pathname;
    config._jobStorageKey = config._jobStorageKey || `background-job-request:${config._jobScope}:${path}:${await identity(config.data)}`;
    if (config._jobScope !== scope()) throw new axios.CanceledError("Merchant or account changed before the job was submitted");
    config._jobKey = config._jobKey || savedKey(config._jobStorageKey) || crypto.randomUUID();
    remember(config._jobStorageKey, config._jobKey);
    config.headers["X-Background-Job"] = "queue";
    config.headers["X-Background-Key"] = config._jobKey;
    config.headers["X-Background-Origin"] = config._jobOrigin;
    return config;
  }, undefined, { runWhen: config => config.backgroundJobs !== false && config.method?.toLowerCase() === "post" &&
    (backgroundPath(String(config.url || "")) || booking.test(String(config.url || ""))) });
  client.interceptors.response.use(async response => {
    const job = response.data?.background_job;
    if (response.status !== 202 || !job?.id) return response;
    const config = response.config;
    const checkScope = () => { if (config._jobScope !== scope()) throw new axios.CanceledError("Merchant or account changed; the saved job continues in its own workspace"); };
    checkScope();
    jobEvent();
    // Upload forms can acknowledge durable acceptance without waiting for row
    // validation. The owner-scoped source history reopens the saved result.
    if (config.backgroundJobMode === "accept") {
      remember(config._jobStorageKey, null);
      return response;
    }
    let current = job;
    try {
      while (!finished.has(current.status)) {
        checkScope();
        await delay(document.visibilityState === "hidden" ? 5000 : 1200, config.signal);
        checkScope();
        current = (await client.get(`/background-jobs/${job.id}`, { signal: config.signal, globalLoader: false })).data;
      }
      jobEvent();
      checkScope();
      if (current.status === "CANCELLED") { remember(config._jobStorageKey, null); throw new Error("Background job was cancelled. Completed records are preserved."); }
      const result = await client.get(`/background-jobs/${job.id}/result`, { signal: config.signal, globalLoader: false });
      remember(config._jobStorageKey, null);
      return { ...response, status: result.status, data: result.data };
    } catch (error) {
      if (error.response?.status >= 400 && error.response?.status < 500 && finished.has(current.status)) remember(config._jobStorageKey, null);
      throw error;
    }
  }, async error => {
    const config = error.config;
    if (config && config.backgroundJobs !== false && !config._jobRecovery && config.method?.toLowerCase() === "post" && booking.test(String(config.url || "")) &&
      !axios.isCancel(error) && (!error.response || [408, 502, 503, 504].includes(error.response.status))) {
      if (config._bookingScope !== scope()) throw new axios.CanceledError("Merchant or account changed; booking recovery stays in the original workspace");
      let body;
      try { body = typeof config.data === "string" ? JSON.parse(config.data) : config.data; } catch { throw error; }
      if (!body?.idempotency_key) throw error;
      return client.request({ ...config, _jobRecovery: true, timeout: 30000, data: body });
    }
    throw error;
  });
}
